/**
 * Storezone — Tenant Context
 *
 * Manages the currently active tenant for the logged-in user.
 * A user may belong to multiple tenants and can switch between them.
 *
 * Also exposes:
 * - Active branch selection
 * - Enabled modules for the active tenant
 * - Helper methods: hasPermission(), hasModule(), isAdmin(), isSeller()
 */

import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import type { ModuleKey, TenantMembershipSummary } from '@/types'
import type { TenantBrandingUpdate } from '@/types/settings'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/lib/api'

export type TenantBranding = Partial<TenantBrandingUpdate> & {
  logo_url?: string
  favicon_url?: string
  [key: string]: any
}

// Added shape for the backend context
interface BackendTenantContext {
  tenant_id: string
  membership_id: string | null
  role_key: string
  role_name: string
  is_superadmin: boolean
  permissions: string[]
  enabled_modules: string[]
  branding?: TenantBranding
}

// ============================================================
// Context Shape
// ============================================================

export interface TenantContextValue {
  /** The currently active tenant membership */
  activeMembership: TenantMembershipSummary | null

  /** ID of the currently active tenant */
  tenantId: string | null

  /** All memberships — for tenant switcher UI */
  memberships: TenantMembershipSummary[]

  /** The currently active branch ID (if branching is enabled) */
  activeBranchId: string | null

  /** The permissions granted to the active user in this tenant */
  permissions: string[]

  /** The modules enabled for this tenant */
  enabledModules: string[]

  /** The tenant's branding configuration */
  branding: TenantBranding | null

  /** Switch to a different tenant */
  switchTenant: (tenantId: string) => void

  /** Switch to a different branch */
  switchBranch: (branchId: string) => void

  /** Check if the user is an admin in the active tenant */
  isAdmin: () => boolean

  /** Check if the user is a seller in the active tenant */
  isSeller: () => boolean

  /** Check if a module is in the active membership's context */
  hasModule: (module: ModuleKey) => boolean

  /** Check if a specific permission is granted */
  hasPermission: (permission: string) => boolean

  /** True if the tenant context is ready */
  ready: boolean
}

// ============================================================
// Context
// ============================================================

export const TenantContext = createContext<TenantContextValue | null>(null)

// ============================================================
// Storage Keys
// ============================================================

const ACTIVE_TENANT_KEY = 'storezone:active_tenant_id'
const ACTIVE_BRANCH_KEY = 'storezone:active_branch_id'

// ============================================================
// Provider
// ============================================================

interface TenantProviderProps {
  children: ReactNode
}

export function TenantProvider({ children }: TenantProviderProps) {
  const { memberships, loading } = useAuth()
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null)
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null)
  
  const [permissions, setPermissions] = useState<string[]>([])
  const [enabledModules, setEnabledModules] = useState<string[]>([])
  const [branding, setBranding] = useState<Record<string, unknown> | null>(null)
  const [contextLoading, setContextLoading] = useState(false)

  // --------------------------------------------------------
  // Fetch Full Context from Backend
  // --------------------------------------------------------
  const fetchTenantContext = useCallback(async (tenantId: string) => {
    setContextLoading(true)
    try {
      const ctx = await apiClient.get<BackendTenantContext>(
        `/api/v1/tenants/context?tenant_id=${tenantId}`
      )
      setPermissions(ctx.permissions || [])
      setEnabledModules(ctx.enabled_modules || [])
      setBranding(ctx.branding || null)
    } catch (err) {
      console.error('Failed to resolve tenant context', err)
      setPermissions([])
      setEnabledModules([])
      setBranding(null)
    } finally {
      setContextLoading(false)
    }
  }, [])

  // --------------------------------------------------------
  // Restore active tenant from localStorage on load
  // --------------------------------------------------------
  useEffect(() => {
    if (loading) return
    if (memberships.length === 0) {
      setActiveTenantId(null)
      setActiveBranchId(null)
      return
    }

    const storedTenantId = localStorage.getItem(ACTIVE_TENANT_KEY)
    const storedBranchId = localStorage.getItem(ACTIVE_BRANCH_KEY)

    // Verify the stored tenant ID is still a valid membership
    const validMembership = memberships.find(
      (m) => m.tenant_id === storedTenantId,
    )

    if (validMembership) {
      setActiveTenantId(validMembership.tenant_id)
      setActiveBranchId(storedBranchId)
      localStorage.setItem(ACTIVE_TENANT_KEY, validMembership.tenant_id)
      fetchTenantContext(validMembership.tenant_id)
    } else {
      // Default to first membership
      setActiveTenantId(memberships[0].tenant_id)
      setActiveBranchId(null)
      localStorage.setItem(ACTIVE_TENANT_KEY, memberships[0].tenant_id)
      localStorage.removeItem(ACTIVE_BRANCH_KEY)
      fetchTenantContext(memberships[0].tenant_id)
    }
  }, [memberships, loading, fetchTenantContext])

  // --------------------------------------------------------
  // Dynamic Favicon
  // --------------------------------------------------------
  useEffect(() => {
    if (branding?.favicon_url) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = branding.favicon_url as string
    }
  }, [branding?.favicon_url])

  // --------------------------------------------------------
  // Active membership
  // --------------------------------------------------------
  const activeMembership =
    memberships.find((m) => m.tenant_id === activeTenantId) ?? null

  // --------------------------------------------------------
  // Switch Tenant
  // --------------------------------------------------------
  const switchTenant = useCallback(
    (tenantId: string) => {
      const membership = memberships.find((m) => m.tenant_id === tenantId)
      if (!membership) return
      setActiveTenantId(tenantId)
      setActiveBranchId(null)
      localStorage.setItem(ACTIVE_TENANT_KEY, tenantId)
      localStorage.removeItem(ACTIVE_BRANCH_KEY)
      fetchTenantContext(tenantId)
    },
    [memberships, fetchTenantContext],
  )

  // --------------------------------------------------------
  // Switch Branch
  // --------------------------------------------------------
  const switchBranch = useCallback((branchId: string) => {
    setActiveBranchId(branchId)
    localStorage.setItem(ACTIVE_BRANCH_KEY, branchId)
  }, [])

  // --------------------------------------------------------
  // Role Helpers
  // --------------------------------------------------------
  const isAdmin = useCallback((): boolean => {
    if (!activeMembership) return false
    return (
      activeMembership.role_key === 'admin' ||
      activeMembership.role_key === 'superadmin'
    )
  }, [activeMembership])

  const isSeller = useCallback((): boolean => {
    return activeMembership?.role_key === 'seller'
  }, [activeMembership])

  // --------------------------------------------------------
  // Module helper — driven by backend enablement
  // --------------------------------------------------------
  const hasModule = useCallback(
    (module: ModuleKey): boolean => {
      return enabledModules.includes(module)
    },
    [enabledModules],
  )

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission)
    },
    [permissions],
  )

  const ready = !loading && !contextLoading && activeTenantId !== null

  const value: TenantContextValue = {
    activeMembership,
    tenantId: activeTenantId,
    memberships,
    activeBranchId,
    permissions,
    enabledModules,
    branding,
    switchTenant,
    switchBranch,
    isAdmin,
    isSeller,
    hasModule,
    hasPermission,
    ready,
  }

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  )
}
